import { Fragment, useState } from 'react';
import { useClickOutside } from '../../../hooks';
import styles from './ZapCard.module.scss';
import type { AnchorTarget } from '../../../ts-types';
import { Avatar } from '../../display/Avatar';
import { Heading } from '../../display/Heading';
import type { IconName } from '../../display/Icon';
import { Icon } from '../../display/Icon';
import { Tag } from '../../display/Tag';
import { TooltipWrapper } from '../../display/TooltipWrapper';
import { Menu, MenuItem } from '../../forms/Menu';
import { LegacyButton } from '../../navigation/LegacyButton';
import { Link } from '../../navigation/Link';
import { ServiceIcons } from '@/components/patterns/ServiceIcons';
import { useResizeObserver } from './useResizeObserver';
import { darkenColor } from './utils/darkenColor';

type Service = {
  name: string;
  src: string;
};

type Services = Array<Service>;

type MenuItem = {
  text: string;
  icon: IconName;
  href?: string;
  onClick?: (e: React.MouseEvent, item: MenuItem) => void;
  rel?: string;
};

type RenderMenuProps = {
  closeMenu: () => void;
};

type Layout = 'auto' | 'card' | 'line' | 'mega';
type Width = 'card' | 'wide' | 'large-line' | 'medium-line';

export type Props = {
  /**
   * URL pointing to the author's avatar image.
   */
  authorAvatarUrl?: string;
  /**
   * Name of the Zap's author.
   */
  authorName?: string;
  /**
   * Function to render the author, which may render above
   * the card or within it, depending on the `layout` prop.
   */
  renderAuthor?: (
    props: Props,
    computedState: ComputedState
  ) => React.ReactNode;
  /**
   * Whether clicking outside of an expanded `ZapCard` closes it.
   */
  canClickOutsideToClose?: boolean;
  /**
   * Color of the card. Typically neutral100 or white, but can be any color
   * to support platform apps' branding. **Note:** `ZapCard`
   * will only be colored when its `layout` is `card`.
   */
  color?: string;
  /**
   * Detailed content to render about the Zap, such as its steps,
   * a description of it, etc. `children` will only render if
   * the current `layout` is `line` (or `auto` and enough space
   * exists to render a `line` `layout`) and the user has expanded
   * `ZapCard` by clicking on it.
   */
  children?: React.ReactNode;
  /**
   * URL to the shared Zap / template.
   */
  href: string;
  /**
   * URL target. defaults to _self
   */
  hrefTarget?: AnchorTarget;
  /**
   * Optional click handler for when `ZapCard` is clicked.
   * Use for things like event tracking.
   */
  onClick?: (e: React.MouseEvent, computedState: ComputedState) => void;
  /**
   * Optional label to apply to the card. Use for things like "Premium"
   */
  label?: string;
  /**
   * Optional tooltip to render with the label. Use to
   * describe why the `label` has been applied.
   */
  labelTooltip?: string;
  /**
   * Function to render the label. Override to customize how the `label`
   * and `labelTooltip` props render.
   */
  renderLabel?: (props: Props, computedState: ComputedState) => React.ReactNode;
  /**
   * Optionally adds overlay link to entire component so that ZapCard href
   * is navigated to when clicking anywhere on the component (defaults to true).
   */
  shouldOverlayLink?: boolean;
  /**
   * Optionally adds details link next to label. Link is moved to overflow menu
   * on smaller window dimensions.
   */
  detailsLink?: {
    href: string;
    onClick: (e: React.MouseEvent, computedState: ComputedState) => void;
    rel?: string;
  };
  /**
   * How `ZapCard` should render—as a squarish `card` or as a single `line`.
   * Should generally be left as default `auto` value for responsiveness.
   */
  layout?: Layout;
  /**
   * Array of items to render within the Zap's menu.
   */
  menuItems?: Array<MenuItem>;
  /**
   * Function to render the Zap's menu and its items. Override to
   * customize how the menu renders.
   */
  renderMenu?: (
    props: Props,
    computedState: ComputedState,
    renderMenuProps: RenderMenuProps
  ) => React.ReactNode;
  /**
   * Short additional information about the Zap, such as its category,
   * number of users using it, etc.
   */
  metaInfo?: React.ReactNode;
  /**
   * Function to render meta information about a Zap. Override to
   * customize how meta info renders.
   */
  renderMetaInfo?: (
    props: Props,
    computedState: ComputedState
  ) => React.ReactNode;
  /**
   * Array of services used by the Zap. Used to display service icons.
   */
  services: Services;
  /**
   * Title of the Zap.
   */
  title: string;
};

// Represents state that's computed at render time.
type ComputedColors = {
  backgroundColor: string;
  backgroundColorDark: string;
};
export type ComputedState = ComputedColors & {
  isExpanded: boolean;
  isMenuOpen: boolean;
  layout: Layout;
  width: number;
  uniqueServices: Array<string>;
};

/* Utility functions
-------------------------------------------------- */
const DEFAULT_COLOR = '#fffdf9';

const isDefaultColor = (color: Props['color']) => {
  return color === DEFAULT_COLOR;
};

const getDefaultComputedColors = (): ComputedColors => ({
  backgroundColor: DEFAULT_COLOR,
  backgroundColorDark: 'rgb(0, 0, 0, 0.25)',
});

const getComputedColors = (color: Props['color']): ComputedColors => {
  if (isDefaultColor(color) || !color) {
    return getDefaultComputedColors();
  }
  // Some of these functions may fail if bad input
  // is provided or if accessible contrast cannot
  // be achieved.
  try {
    return {
      backgroundColor: darkenColor(color, { contrast: 3 }),
      backgroundColorDark: darkenColor(color, {
        contrast: 4.5,
        minimumLightnessDistance: 10,
      }),
    };
  } catch (err) {
    return getDefaultComputedColors();
  }
};

const hasMenu = (menuItems: Props['menuItems']) => {
  return (menuItems || []).length > 0;
};

/* Layout functions and sizes
-------------------------------------------------- */

const CARD_MAX_WIDTH = 375;
const SMALL_LINE_MAX_WIDTH = 770;
const MEDIUM_LINE_MAX_WIDTH = 615;

const isCardLayout = (computedState: ComputedState) => {
  return computedState.layout === 'card';
};

const isLineLayout = (computedState: ComputedState) => {
  return computedState.layout === 'line';
};

const isMegaLayout = (computedState: ComputedState) => {
  return computedState.layout === 'mega';
};

const isCardLayoutWidth = (computedState: ComputedState) => {
  return computedState.width <= CARD_MAX_WIDTH;
};

const isWideLayoutWidth = (computedState: ComputedState) =>
  computedState.width > SMALL_LINE_MAX_WIDTH;

const isMediumLineLayoutWidth = (computedState: ComputedState) => {
  return (
    computedState.width > CARD_MAX_WIDTH &&
    computedState.width <= MEDIUM_LINE_MAX_WIDTH
  );
};

const isLargeLineLayoutWidth = (computedState: ComputedState) => {
  return computedState.width > MEDIUM_LINE_MAX_WIDTH;
};

const getWidth = (computedState: ComputedState): Width => {
  let width: Width = 'card';

  if (isMediumLineLayoutWidth(computedState)) width = 'medium-line';
  else if (isLargeLineLayoutWidth(computedState)) width = 'large-line';
  else if (isWideLayoutWidth(computedState)) width = 'wide';
  else if (isCardLayoutWidth(computedState)) width = 'card';

  return width;
};

const getLayout = (computedState: ComputedState): Layout => {
  return computedState.layout;
};

// Elements change layout based on the size of the element.
// Some move to different locations and some hide. To prevent
// grid gaps from butting up against each other, multiple
// layouts are needed.
const computeLineLayoutGrid = (computedState: ComputedState, props: Props) => {
  if (isLargeLineLayoutWidth(computedState)) {
    return {
      '--_zds-line-layout-columns':
        'max-content max-content 1fr max-content max-content max-content',
      '--_zds-line-layout-areas': `
      'services title title title label cta menu'
        '.        ${
          props.authorName ? 'author' : 'meta'
        }  meta  meta   meta     .   .'`,
    };
  }
  return {
    '--_zds-line-layout-columns': 'max-content 1fr max-content',
    '--_zds-line-layout-areas': '"services title cta"',
  };
};

const isCardOrMegaLayout = (computedState: ComputedState) => {
  return isCardLayout(computedState) || isMegaLayout(computedState);
};

// Only `card` or `mega` layouts that don't use the default color
// should be colored.
const shouldBeColored = (computedState: ComputedState) => {
  return (
    isCardOrMegaLayout(computedState) &&
    !isDefaultColor(computedState.backgroundColor)
  );
};

type ZapCardCtaProps = {
  ariaHidden: boolean;
  href: string;
  target: AnchorTarget;
  computedState: ComputedState;
  isLink: boolean;
  shouldOverlayLink: boolean;
};

const ZapCardCTA = (props: ZapCardCtaProps) => {
  const { computedState, ariaHidden, href, isLink, target } = props;

  const ctaBody = isLink ? (
    <Fragment>
      <Link
        className={styles.ctaLink}
        href={href}
        target={target}
        tabIndex={ariaHidden ? -1 : undefined}
        data-layout={getLayout(computedState)}
        data-width={getWidth(computedState)}
      >
        Try it
        <span
          className={styles.ctaIcon}
          data-layout={getLayout(computedState)}
          data-zds
        >
          <Icon isBlock={true} name="arrowBigRight" size={24} />
        </span>
      </Link>
    </Fragment>
  ) : (
    <LegacyButton
      href={props.href}
      target={target}
      tabIndex={ariaHidden ? -1 : undefined}
      size="medium"
    >
      Try it
    </LegacyButton>
  );

  return (
    // Add `aria-hidden` to avoid redundant CTAs if it's already handled by `link`.
    // Also add `tabIndex={-1}` to the `Link` or `Button` since nodes with `aria-hidden` on them
    // shouldn't contain interactive elements.
    <div
      aria-hidden={ariaHidden}
      className={styles.ctaArea}
      data-layout={getLayout(computedState)}
      data-overlay-link={props.shouldOverlayLink}
      data-zds
    >
      {ctaBody}
    </div>
  );
};

const defaultRenderAuthor = (props: Props, computedState: ComputedState) => {
  if (!props.authorName) {
    return null;
  }
  // This is a simple implementation that doesn't worry about compound names.
  const [first, ...rest] = props.authorName.split(/\s+/);
  const last = rest.pop();
  const lastInitial = last ? `${last[0]}.` : '';
  const name = `${first} ${lastInitial}`.trim();
  const author = (
    <p
      aria-hidden={true}
      className={styles.authorByLine}
      data-layout={getLayout(computedState)}
      data-zds
    >
      by{' '}
      <span className={styles.authorName} data-zds>
        {name}
      </span>
    </p>
  );

  return (
    <div
      aria-label={`by ${props.authorName}`}
      className={styles.authorContent}
      data-layout={getLayout(computedState)}
      data-width={getWidth(computedState)}
      data-zds
    >
      <Avatar
        aria-hidden={true}
        isBlock={true}
        name={props.authorName}
        size="small"
        url={props.authorAvatarUrl}
      />

      <TooltipWrapper
        content={props.authorName}
        position="southwest"
        shouldCorrectPosition={false}
      >
        {() => author}
      </TooltipWrapper>
    </div>
  );
};

const defaultRenderLabel = (props: Props, computedState: ComputedState) => {
  const color = shouldBeColored(computedState) ? 'inverted' : 'default';

  return (
    props.label && (
      <TooltipWrapper
        content={props.labelTooltip}
        position="southeast"
        shouldCorrectPosition={false}
      >
        {() => <Tag variant={color}>{props.label}</Tag>}
      </TooltipWrapper>
    )
  );
};

const defaultRenderMenu = (
  props: Props,
  computedState: ComputedState,
  { closeMenu }: RenderMenuProps
) => {
  const hasDetailsMenuItem =
    props.detailsLink &&
    isLineLayout(computedState) &&
    isMediumLineLayoutWidth(computedState);

  if (!hasDetailsMenuItem && !hasMenu(props.menuItems)) {
    return null;
  }

  const renderMenuItem = (item: MenuItem) => {
    const onClick = (e: React.MouseEvent) => {
      // Optional chaining not supported by eslint yet.
      // eslint-disable-next-line no-unused-expressions
      item.onClick && item.onClick(e, item);
      closeMenu();
    };
    const icon = item.icon && <Icon name={item.icon} size={24} />;
    return (
      <MenuItem
        href={item.href}
        key={item.text}
        onClick={onClick}
        rel={item.rel}
      >
        <span className={styles.menuItemInner} data-zds>
          {icon}
          <span className={styles.menuItemText} data-zds>
            {item.text}
          </span>
        </span>
      </MenuItem>
    );
  };
  const items = (props.menuItems ?? []).map(renderMenuItem);

  return (
    <div
      className={styles.menu}
      data-layout={getLayout(computedState)}
      data-width={getWidth(computedState)}
      data-zds
    >
      <Menu aria-label="Zap options">
        <div className={styles.menuInner} data-zds>
          {items}
          {hasDetailsMenuItem &&
            renderMenuItem({
              text: 'See Zap details',
              icon: 'miscList' as IconName,
              href: props.detailsLink?.href,
              rel: props.detailsLink?.rel,
              onClick: (e) => props.detailsLink?.onClick(e, computedState),
            })}
        </div>
      </Menu>
    </div>
  );
};

const defaultRenderMetaInfo = (props: Props, computedState: ComputedState) => {
  if (props.metaInfo) {
    return props.metaInfo;
  }
  const truncatedServices = computedState.uniqueServices.slice(0, 3);
  const countDiff =
    computedState.uniqueServices.length - truncatedServices.length;
  return [...truncatedServices, countDiff > 0 && `${countDiff} more`]
    .filter(Boolean)
    .join(' + ');
};

/**
 * Card representing a Zap, Shared Zap, Zap Template, etc.
 */
export const ZapCard = ({
  authorName,
  authorAvatarUrl,
  canClickOutsideToClose,
  children,
  color = DEFAULT_COLOR,
  detailsLink,
  href,
  hrefTarget = '_self',
  label,
  labelTooltip,
  layout = 'auto',
  menuItems = [],
  metaInfo,
  onClick,
  renderAuthor,
  renderLabel,
  renderMenu,
  renderMetaInfo,
  services,
  shouldOverlayLink = true,
  title,
}: Props) => {
  // Destucturing above is necessary to properly display default props in Styleguideist and Storybook.

  const colorValue = color || DEFAULT_COLOR;

  // Restructuring below is necessary because the render prop functions expect all props to be passed in. Restructuring this way avoids a breaking change.
  const allProps = {
    authorName,
    authorAvatarUrl,
    canClickOutsideToClose,
    children,
    color,
    detailsLink,
    href,
    hrefTarget,
    label,
    labelTooltip,
    layout,
    menuItems,
    metaInfo,
    onClick,
    renderAuthor,
    renderLabel,
    renderMenu,
    renderMetaInfo,
    services,
    title,
  };

  // Menu code
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = () => setIsMenuOpen(false);
  const [setMenuOutsideClickNode] = useClickOutside({
    onClickOutside: closeMenu,
  });

  let menuNode: HTMLDivElement | null = null;
  const setMenuNode = (node: HTMLDivElement) => {
    menuNode = node;
    setMenuOutsideClickNode(node);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!menuNode || !menuNode.contains(e.target as Node)) {
      // Optional chaining not supported by eslint yet.
      // eslint-disable-next-line no-unused-expressions, no-use-before-define
      onClick && onClick(e, computedState);
    }
  };

  // Expansion code 1
  const [isExpanded, setIsExpanded] = useState(false);
  const [setExpansionOutsideClickNode] = useClickOutside({
    onClickOutside: () => {
      if (canClickOutsideToClose) {
        setIsExpanded(false);
      }
    },
  });

  // Dynamic sizing
  const { ref, width } = useResizeObserver();
  const uniqueServices = [
    ...new Set(
      services.map((service) => {
        return service.name;
      })
    ),
  ];
  const computedState: ComputedState = {
    isExpanded,
    isMenuOpen,
    layout: layout as Layout,
    ...getComputedColors(colorValue),
    width,
    uniqueServices,
  };
  // Reassigned to pass `computedState` into `isCardLayoutWidth`.
  computedState.layout =
    layout !== 'auto'
      ? (layout as Layout)
      : isCardLayoutWidth(computedState)
        ? 'card'
        : 'line';

  // Expansion code 2
  const canExpand = children && isLineLayout(computedState);

  // Dynamic nodes and content
  const renderedMenu = renderMenu
    ? renderMenu(allProps, computedState, { closeMenu })
    : defaultRenderMenu(allProps, computedState, { closeMenu });
  const renderedLabel = renderLabel
    ? renderLabel(allProps, computedState)
    : defaultRenderLabel(allProps, computedState);
  const renderedAuthor = renderAuthor
    ? renderAuthor(allProps, computedState)
    : defaultRenderAuthor(allProps, computedState);
  const renderedMetaInfo = renderMetaInfo
    ? renderMetaInfo(allProps, computedState)
    : defaultRenderMetaInfo(allProps, computedState);

  const DetailsLink = detailsLink && (
    <span className={styles.detailsHref} data-zds>
      <Link
        href={detailsLink.href}
        rel={detailsLink.rel}
        onClick={(e) => detailsLink?.onClick(e, computedState)}
      >
        Details
      </Link>
    </span>
  );

  const link = shouldOverlayLink &&
    (isCardLayout(computedState) || !children) && (
      <Link className={styles.link} href={href} target={hrefTarget}>
        {title}
      </Link>
    );

  const details = canExpand && (
    <details data-testid="ZapCard__details" open={isExpanded} data-zds>
      <summary
        className={styles.summary}
        onClick={(e) => {
          e.preventDefault();
          setIsExpanded(!isExpanded);
        }}
        data-zds
      >
        More details
      </summary>
      <div className={styles.detailsContent} data-zds>
        {children}
      </div>
    </details>
  );

  const cta = isCardOrMegaLayout(computedState) ? (
    <ZapCardCTA
      computedState={computedState}
      ariaHidden={!!link}
      href={href}
      isLink={true}
      shouldOverlayLink={shouldOverlayLink}
      target={hrefTarget}
    />
  ) : isLineLayout(computedState) ? (
    <ZapCardCTA
      computedState={computedState}
      ariaHidden={!!link}
      href={href}
      isLink={false}
      shouldOverlayLink={shouldOverlayLink}
      target={hrefTarget}
    />
  ) : null;

  const serviceIconSize = isMegaLayout(computedState) ? 'large' : 'medium';
  const rootHasTopPadding =
    (isCardOrMegaLayout(computedState) ||
      (isLineLayout(computedState) &&
        isMediumLineLayoutWidth(computedState))) &&
    (hasMenu(allProps.menuItems) || allProps.authorName);

  // Use eslint-disable below since accessibility is handled elsewhere.
  return (
    <article
      className={styles.root}
      ref={ref}
      data-has-top-padding={rootHasTopPadding}
      data-zds
    >
      {/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */}
      <div
        className={styles.inner}
        data-testid="ZapCard__inner"
        data-colored={shouldBeColored(computedState)}
        data-layout={getLayout(computedState)}
        data-expanded={computedState.isExpanded}
        style={{
          '--_zds-zapcard-background-color': computedState.backgroundColor,
          '--_zds-zapcard-background-color-dark':
            computedState.backgroundColorDark,
        }}
        onClick={handleClick}
        ref={setExpansionOutsideClickNode}
        data-zds
      >
        <div
          className={styles.layout}
          data-layout={getLayout(computedState)}
          data-width={getWidth(computedState)}
          style={computeLineLayoutGrid(computedState, allProps)}
          data-zds
        >
          <div
            className={styles.titleArea}
            data-layout={getLayout(computedState)}
            data-zds
          >
            <Heading
              className={styles.title}
              data-layout={getLayout(computedState)}
              data-width={getWidth(computedState)}
            >
              {title}
            </Heading>
          </div>
          {renderedAuthor && (
            <div
              className={styles.authorArea}
              data-layout={getLayout(computedState)}
              data-width={getWidth(computedState)}
              data-zds
            >
              {renderedAuthor}
            </div>
          )}
          <div
            className={styles.labelArea}
            data-layout={getLayout(computedState)}
            data-width={getWidth(computedState)}
            data-zds
          >
            {renderedLabel}
            {DetailsLink}
          </div>
          {link}
          {cta}
          <div
            className={styles.menuArea}
            ref={setMenuNode}
            data-layout={getLayout(computedState)}
            data-width={getWidth(computedState)}
            data-zds
          >
            {renderedMenu && (
              <button
                aria-expanded={isMenuOpen}
                aria-label="More options"
                className={styles.menuToggle}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                type="button"
                data-zds
              >
                <Icon name="navMoreHoriz" size={32} />
              </button>
            )}
            {isMenuOpen && renderedMenu}
          </div>
          <div className={styles.servicesArea} data-zds>
            <TooltipWrapper
              allowMultilineTooltip={true}
              content={uniqueServices.join(', ')}
              position="southwest"
              shouldCorrectPosition={false}
              tooltipId="tooltip-zap-card"
            >
              {() => (
                <ServiceIcons
                  aria-label={`Zap uses ${uniqueServices.join(', ')}`}
                  maxServices={3}
                  services={services}
                  size={serviceIconSize}
                  hasBorder={!shouldBeColored(computedState)}
                />
              )}
            </TooltipWrapper>
          </div>
          <div
            className={styles.metaInfoArea}
            data-layout={getLayout(computedState)}
            data-width={getWidth(computedState)}
            data-zds
          >
            {renderedMetaInfo}
          </div>
        </div>
        {details}
      </div>
    </article>
  );
};
